/**
 * Employee Authentication Context
 * Manages employee login state and authentication
 */

import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Employee, EmployeeLoginRequest } from '../services/types';
import { employeeApi } from '../services/employeeApi';

interface EmployeeAuthContextType {
    employee: Employee | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (credentials: EmployeeLoginRequest) => Promise<{ success: boolean; message?: string }>;
    logout: () => Promise<void>;
    updateEmployee: (employee: Employee) => void;
}

const EmployeeAuthContext = createContext<EmployeeAuthContextType | undefined>(undefined);

export const EmployeeAuthProvider = ({ children }: { children: ReactNode }) => {
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check if employee is logged in on mount
    useEffect(() => {
        const checkAuth = async () => {
            const storedEmployee = employeeApi.getStoredEmployee();
            const isLoggedIn = employeeApi.isLoggedIn();

            if (isLoggedIn && storedEmployee) {
                // Verify token by fetching profile
                try {
                    const response = await employeeApi.getProfile();
                    if (response.status && response.data) {
                        setEmployee(response.data);
                    } else {
                        // Token invalid, clear storage
                        localStorage.removeItem('employee_access_token');
                        localStorage.removeItem('employee_refresh_token');
                        localStorage.removeItem('employee_data');
                    }
                } catch (error) {
                    console.error('Auth check failed:', error);
                }
            }

            setIsLoading(false);
        };

        checkAuth();
    }, []);

    const login = async (credentials: EmployeeLoginRequest) => {
        try {
            const response = await employeeApi.login(credentials);

            if (response.status && response.data) {
                setEmployee(response.data.employee);
                return { success: true };
            } else {
                return {
                    success: false,
                    message: response.message || 'Login failed'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                message: 'An error occurred during login'
            };
        }
    };

    const logout = async () => {
        try {
            await employeeApi.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setEmployee(null);
        }
    };

    const updateEmployee = (updatedEmployee: Employee) => {
        setEmployee(updatedEmployee);
        localStorage.setItem('employee_data', JSON.stringify(updatedEmployee));
    };

    const value: EmployeeAuthContextType = {
        employee,
        isLoading,
        isAuthenticated: !!employee,
        login,
        logout,
        updateEmployee
    };

    return (
        <EmployeeAuthContext.Provider value={value}>
            {children}
        </EmployeeAuthContext.Provider>
    );
};

export const useEmployeeAuth = () => {
    const context = useContext(EmployeeAuthContext);
    if (context === undefined) {
        throw new Error('useEmployeeAuth must be used within an EmployeeAuthProvider');
    }
    return context;
};
